import {
  ApiError,
  type DurationStats,
  type MessageAuthor,
  type ThreadKind,
  type TicketReport,
  type TicketReportCounts,
  type TicketReportQuery,
} from '@central/api';
import { Card, KpiTile, PageHeader, color, formatDateTime } from '@central/ui';
import { Alert, Button, Select, Table, TextInput } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { DataTable, Empty, QueryState } from '../components/parts';
import { priorityChip } from '../lib/inbox';
import { mono, muted } from '../lib/labels';
import {
  formatElapsed,
  kindLabel,
  reportQuery,
  reportStatuses,
  secondsBetween,
  statusLabel,
  ticketKinds,
  type ReportStatus,
  type ReportsSearch,
} from '../lib/reports';

const fieldText: Record<string, string> = {
  from: 'A day, at most 366 days before To',
  to: 'On or after From, at most 366 days later',
};

/**
 * Support report: tickets reported or resolved in a range of UTC days, with
 * totals, first-response and resolution times, breakdowns by customer and
 * type, and the ticket list. Filters live in the URL. Every staff role reads it.
 */
export function Reports() {
  const api = useApi();
  const navigate = useNavigate({ from: '/reports' });
  const search = useSearch({ from: '/app/reports' });
  return (
    <TicketReportPage
      search={search}
      apply={(next) => void navigate({ search: (s) => ({ ...s, ...next }), replace: true })}
      subtitle="Support tickets reported or resolved in a period (UTC days)"
      queryKey={keys.ticketReport}
      fetch={(q) => api.reports.tickets(q)}
      csvUrl={(q) => api.reports.ticketsCsvUrl(q)}
      pdfUrl={(q) => api.reports.ticketsPdfUrl(q)}
      kinds={ticketKinds}
      customerLink={(id, name) => (
        <Link to="/customers/$customerId" params={{ customerId: id }} style={{ color: color.link }}>
          {name}
        </Link>
      )}
    />
  );
}

/** What a support report page reads and links to; the Rethink and partner pages differ only here. */
export interface TicketReportSource {
  search: ReportsSearch;
  apply: (next: Partial<ReportsSearch>) => void;
  subtitle: string;
  queryKey: (q: TicketReportQuery) => readonly unknown[];
  fetch: (q: TicketReportQuery) => Promise<TicketReport>;
  csvUrl: (q: TicketReportQuery) => string;
  pdfUrl: (q: TicketReportQuery) => string;
  /** The ticket types the Type filter offers. */
  kinds: Array<{ value: ThreadKind; label: string }>;
  /** A customer's name in the per-customer table, linked to its page. */
  customerLink: (id: string, name: string) => ReactNode;
}

/** The support report page: filters, downloads, totals, breakdowns and tickets. */
export function TicketReportPage(p: TicketReportSource) {
  const { search, apply } = p;
  // The default range resolves once per search, so the query key stays put.
  const query = useMemo(() => reportQuery(search), [search]);
  const report = useQuery({
    queryKey: p.queryKey(query),
    queryFn: () => p.fetch(query),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const fields = report.error instanceof ApiError ? (report.error.fields ?? {}) : {};

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle={p.subtitle}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button component="a" href={p.csvUrl(query)} download variant="default">
              Download CSV
            </Button>
            <Button component="a" href={p.pdfUrl(query)} download color="dark">
              Download PDF
            </Button>
          </div>
        }
      />
      <Card>
        <form
          aria-label="Filter the report"
          onSubmit={(e) => e.preventDefault()}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
            alignItems: 'start',
          }}
        >
          <TextInput
            type="date"
            label="From"
            value={query.from}
            error={fields.from && fieldText.from}
            onChange={(e) => e.currentTarget.value && apply({ from: e.currentTarget.value })}
          />
          <TextInput
            type="date"
            label="To"
            value={query.to}
            error={fields.to && fieldText.to}
            onChange={(e) => e.currentTarget.value && apply({ to: e.currentTarget.value })}
          />
          <CustomerSelect
            label="Customer"
            placeholder="Any customer"
            value={search.customerId ?? ''}
            onChange={(id) => apply({ customerId: id || undefined })}
          />
          <Select
            label="Type"
            data={[{ value: '', label: 'Any type' }, ...p.kinds]}
            value={search.kind ?? ''}
            onChange={(v) => apply({ kind: (v || undefined) as ThreadKind | undefined })}
            allowDeselect={false}
          />
          <Select
            label="Status now"
            data={[
              { value: '', label: 'Any status' },
              ...Object.entries(reportStatuses).map(([value, s]) => ({ value, label: s.label })),
            ]}
            value={search.status ?? ''}
            onChange={(v) => apply({ status: (v || undefined) as ReportStatus | undefined })}
            allowDeselect={false}
          />
        </form>
      </Card>
      <QueryState isPending={report.isPending} error={report.error}>
        {() => report.data && <ReportBody r={report.data} customerLink={p.customerLink} />}
      </QueryState>
    </>
  );
}

function ReportBody({
  r,
  customerLink,
}: {
  r: TicketReport;
  customerLink: TicketReportSource['customerLink'];
}) {
  const t = r.totals;
  return (
    <>
      <section
        aria-label="Totals"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <KpiTile label="Tickets" value={t.tickets} />
        <KpiTile label="Reported" value={t.opened} />
        <KpiTile label="Resolved" value={t.resolved} />
        <KpiTile label="Still open" value={t.stillOpen} />
        <KpiTile label="Unanswered" value={t.unanswered} />
        <KpiTile label="Median first response" value={median(t.firstResponse)} />
        <KpiTile label="Median resolution" value={median(t.resolution)} />
      </section>
      <p style={{ margin: 0, fontSize: 13, ...muted }}>
        First response: {statsText(t.firstResponse, 'reported')}. Resolution:{' '}
        {statsText(t.resolution, 'resolved')}.
      </p>
      <Card padded={false} title="By customer">
        {r.customers.length === 0 ? (
          <Empty>No tickets.</Empty>
        ) : (
          <DataTable label="By customer" minWidth={760} head={breakdownHead('Customer')}>
            {r.customers.map((g) => (
              <BreakdownRow
                key={g.customer.id}
                name={customerLink(g.customer.id, g.customer.name || g.customer.id)}
                c={g.counts}
              />
            ))}
          </DataTable>
        )}
      </Card>
      <Card padded={false} title="By type">
        {r.kinds.length === 0 ? (
          <Empty>No tickets.</Empty>
        ) : (
          <DataTable label="By type" minWidth={760} head={breakdownHead('Type')}>
            {r.kinds.map((g) => (
              <BreakdownRow key={g.kind} name={kindLabel[g.kind] ?? g.kind} c={g.counts} />
            ))}
          </DataTable>
        )}
      </Card>
      <Card padded={false} title="Tickets">
        {r.truncated && (
          <Alert color="yellow" m="md" title="List cut short">
            Showing the newest {r.tickets.length} of {t.tickets} tickets; the figures above count
            all of them. The downloads list the same {r.maxTickets}. Narrow the period or filters to
            list them all.
          </Alert>
        )}
        {r.tickets.length === 0 ? (
          <Empty>No tickets were reported or resolved in this period.</Empty>
        ) : (
          <DataTable
            label="Tickets"
            minWidth={1080}
            head={[
              'Ticket',
              'Subject',
              'Customer',
              'Type',
              'Status',
              'Priority',
              'Reported',
              'First response',
              'Resolved',
            ]}
          >
            {r.tickets.map((row) => (
              <Table.Tr key={row.id} style={{ verticalAlign: 'top' }}>
                <Table.Td style={{ ...mono, whiteSpace: 'nowrap' }}>
                  <Link
                    to="/inbox/$threadId"
                    params={{ threadId: row.id }}
                    style={{ color: color.link }}
                  >
                    {row.ref}
                  </Link>
                </Table.Td>
                <Table.Td style={{ wordBreak: 'break-word' }}>{row.subject}</Table.Td>
                <Table.Td>{row.customer.name || row.customer.id}</Table.Td>
                <Table.Td>{kindLabel[row.kind] ?? row.kind}</Table.Td>
                <Table.Td>{statusLabel[row.status] ?? row.status}</Table.Td>
                <Table.Td>{priorityChip[row.priority]?.label ?? row.priority}</Table.Td>
                <Table.Td>
                  <When at={row.reportedAt} by={row.reportedBy} />
                </Table.Td>
                <Table.Td>
                  {row.firstResponseAt ? (
                    <When
                      at={row.firstResponseAt}
                      after={secondsBetween(row.reportedAt, row.firstResponseAt)}
                    />
                  ) : (
                    <span style={muted}>–</span>
                  )}
                </Table.Td>
                <Table.Td>
                  {row.resolvedAt ? (
                    <When
                      at={row.resolvedAt}
                      by={row.resolvedBy}
                      after={secondsBetween(row.reportedAt, row.resolvedAt)}
                    />
                  ) : (
                    <span style={muted}>–</span>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </DataTable>
        )}
      </Card>
    </>
  );
}

function breakdownHead(first: string) {
  return [
    first,
    'Tickets',
    'Reported',
    'Resolved',
    'Still open',
    'Median first response',
    'Median resolution',
  ];
}

function BreakdownRow({ name, c }: { name: ReactNode; c: TicketReportCounts }) {
  return (
    <Table.Tr>
      <Table.Td>{name}</Table.Td>
      <Table.Td>{c.tickets}</Table.Td>
      <Table.Td>{c.opened}</Table.Td>
      <Table.Td>{c.resolved}</Table.Td>
      <Table.Td>{c.stillOpen}</Table.Td>
      <Table.Td>{median(c.firstResponse)}</Table.Td>
      <Table.Td>{median(c.resolution)}</Table.Td>
    </Table.Tr>
  );
}

/** A time, who acted and how long after reporting, stacked. */
function When({ at, by, after }: { at: string; by?: MessageAuthor; after?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
      <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(at)}</span>
      {by && <span style={muted}>{authorText(by)}</span>}
      {after !== undefined && <span style={muted}>after {formatElapsed(after)}</span>}
    </div>
  );
}

function authorText(a: MessageAuthor): string {
  if (a.kind === 'staff') return `${a.name || 'Rethink staff'} (Rethink)`;
  if (a.kind === 'system') return a.name || 'GoATMS Central';
  return a.name || 'Customer';
}

function median(s: DurationStats): string {
  return s.count === 0 ? '–' : formatElapsed(s.medianSeconds);
}

/** "median 2 h 0 min, mean 3 h 10 min over 12 tickets reported in the period". */
function statsText(s: DurationStats, verb: string): string {
  const over = `${s.count} ${s.count === 1 ? 'ticket' : 'tickets'} ${verb} in the period`;
  if (s.count === 0) return `no tickets ${verb} in the period`;
  return `median ${formatElapsed(s.medianSeconds)}, mean ${formatElapsed(s.meanSeconds)} over ${over}`;
}
